import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Typography,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Alert,
  Paper,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { QuizQuestion } from '../services/trainingManagementService';

interface QuizQuestionFormProps {
  question?: QuizQuestion | null;
  onSave: (data: Partial<QuizQuestion>) => void;
  onCancel: () => void;
}

export default function QuizQuestionForm({
  question,
  onSave,
  onCancel,
}: QuizQuestionFormProps) {
  const [questionText, setQuestionText] = useState('');
  const [questionType, setQuestionType] = useState<'MULTIPLE_CHOICE' | 'TRUE_FALSE' | 'MULTIPLE_SELECT'>('MULTIPLE_CHOICE');
  const [options, setOptions] = useState<Array<{ id: string; text: string }>>([
    { id: 'a', text: '' },
    { id: 'b', text: '' },
  ]);
  const [correctAnswer, setCorrectAnswer] = useState<string>('');
  const [explanation, setExplanation] = useState('');
  const [points, setPoints] = useState(1);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (question) {
      setQuestionText(question.question);
      setQuestionType(question.questionType);
      setOptions(question.options || []);
      setCorrectAnswer(
        Array.isArray(question.correctAnswer)
          ? question.correctAnswer[0]
          : question.correctAnswer
      );
      setExplanation(question.explanation || '');
      setPoints(question.points);
    }
  }, [question]);

  const handleAddOption = () => {
    const nextId = String.fromCharCode(97 + options.length); // a, b, c, d...
    setOptions([...options, { id: nextId, text: '' }]);
  };

  const handleRemoveOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const updateOption = (index: number, text: string) => {
    const updated = [...options];
    updated[index].text = text;
    setOptions(updated);
  };

  const handleSubmit = () => {
    // Validation
    if (!questionText.trim()) {
      setError('Question text is required');
      return;
    }

    if (options.length < 2) {
      setError('At least 2 options are required');
      return;
    }

    if (options.some(opt => !opt.text.trim())) {
      setError('All options must have text');
      return;
    }

    if (!correctAnswer) {
      setError('Please select the correct answer');
      return;
    }

    setError(null);

    const questionData: Partial<QuizQuestion> = {
      question: questionText,
      questionType,
      options: options.map(opt => ({ id: opt.id, text: opt.text })),
      correctAnswer,
      explanation: explanation || undefined,
      points,
    };

    onSave(questionData);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        {question ? 'Edit Question' : 'Add New Question'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box component="form" noValidate>
        {/* Question Text */}
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Question"
          value={questionText}
          onChange={(e) => setQuestionText(e.target.value)}
          required
          sx={{ mb: 3 }}
        />

        {/* Question Type */}
        <TextField
          fullWidth
          select
          label="Question Type"
          value={questionType}
          onChange={(e) => setQuestionType(e.target.value as any)}
          sx={{ mb: 3 }}
        >
          <MenuItem value="MULTIPLE_CHOICE">Multiple Choice</MenuItem>
          <MenuItem value="TRUE_FALSE">True/False</MenuItem>
          <MenuItem value="MULTIPLE_SELECT">Multiple Select</MenuItem>
        </TextField>

        {/* Answer Options */}
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle2">Answer Options</Typography>
            {options.length < 6 && (
              <Button startIcon={<Add />} size="small" onClick={handleAddOption}>
                Add Option
              </Button>
            )}
          </Box>

          <RadioGroup value={correctAnswer} onChange={(e) => setCorrectAnswer(e.target.value)}>
            {options.map((option, index) => (
              <Box key={option.id} display="flex" gap={1} mb={2} alignItems="center">
                <FormControlLabel
                  value={option.id}
                  control={<Radio />}
                  label=""
                  sx={{ mr: 0 }}
                />
                <TextField
                  fullWidth
                  label={`Option ${option.id.toUpperCase()}`}
                  value={option.text}
                  onChange={(e) => updateOption(index, e.target.value)}
                  required
                />
                {options.length > 2 && (
                  <IconButton onClick={() => handleRemoveOption(index)} color="error" size="small">
                    <Delete />
                  </IconButton>
                )}
              </Box>
            ))}
          </RadioGroup>

          <Typography variant="caption" color="text.secondary">
            Select the radio button next to the correct answer
          </Typography>
        </Box>

        {/* Explanation */}
        <TextField
          fullWidth
          multiline
          rows={2}
          label="Explanation (Optional)"
          value={explanation}
          onChange={(e) => setExplanation(e.target.value)}
          placeholder="Explain why this is the correct answer (shown after quiz submission)"
          sx={{ mb: 3 }}
        />

        {/* Points */}
        <TextField
          fullWidth
          type="number"
          label="Points"
          value={points}
          onChange={(e) => setPoints(parseInt(e.target.value) || 1)}
          inputProps={{ min: 1, max: 100 }}
          sx={{ mb: 3 }}
        />

        {/* Actions */}
        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {question ? 'Update Question' : 'Add Question'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

